/**
 * Content item lifecycle. The one rule that matters: nothing reaches `scheduled` (and so nothing
 * is ever published) without a person's approval, and editing approved content sends it back for
 * approval.
 *
 *   draft ─submit→ in_review ─approve→ approved ─schedule→ scheduled ─(cron)→ publishing → published
 *     ↑                │  └request_changes→ changes_requested ─edit→ draft            └→ failed ─retry→ scheduled
 *     └──────edit──────┴─reject→ rejected
 */

import { CONTENT_ACTIONS, CONTENT_STATUSES, type ContentAction, type ContentStatus } from '@marketingbee/contracts';

export { CONTENT_ACTIONS, CONTENT_STATUSES, type ContentAction, type ContentStatus };

const T: Record<ContentAction, Partial<Record<ContentStatus, ContentStatus>>> = {
  edit: { draft: 'draft', in_review: 'draft', changes_requested: 'draft', approved: 'draft', scheduled: 'draft', failed: 'draft', rejected: 'draft' },
  submit: { draft: 'in_review', changes_requested: 'in_review' },
  approve: { in_review: 'approved' },
  request_changes: { in_review: 'changes_requested', approved: 'changes_requested', scheduled: 'changes_requested' },
  reject: { in_review: 'rejected', approved: 'rejected', scheduled: 'rejected', draft: 'rejected', changes_requested: 'rejected' },
  schedule: { approved: 'scheduled' },
  unschedule: { scheduled: 'approved' },
  start_publish: { scheduled: 'publishing' },
  publish_ok: { publishing: 'published' },
  publish_fail: { publishing: 'failed' },
  retry: { failed: 'scheduled' },
  archive: { draft: 'archived', rejected: 'archived', published: 'archived', failed: 'archived', changes_requested: 'archived', approved: 'archived', in_review: 'archived' },
};

export function nextStatus(from: ContentStatus, action: ContentAction): ContentStatus | null {
  return T[action][from] ?? null;
}

export function allowedActions(from: ContentStatus): ContentAction[] {
  return CONTENT_ACTIONS.filter((a) => T[a][from] !== undefined && !['start_publish', 'publish_ok', 'publish_fail'].includes(a));
}

/** Text can be changed in these states (an edit after approval needs approval again). */
export function isEditable(status: ContentStatus): boolean {
  return T.edit[status] !== undefined;
}

/** Statuses the weekly digest asks a person to act on. */
export const NEEDS_REVIEW: readonly ContentStatus[] = ['in_review'];

/** Publishing retries: 3 attempts, 5 / 30 minutes apart, then `failed` stays failed. */
export const PUBLISH_RETRY_DELAYS_MIN = [5, 30] as const;
